import { type ReactElement, type ReactNode } from "react";
import { useField } from "formik";

import { Dropdown } from "@vega-core/components/Form/Controls/Dropdown/Dropdown/Dropdown";
import {
  SearchableDropdown,
  type SearchableDropdownProps,
} from "@vega-core/components/Form/Controls/SearchableDropdown/SearchableDropdown";
import { useTouchFieldOnSubmit } from "@vega-core/helpers/formik.helper";
import { type OptionType } from "@vega-core/models/options";
import { isTruthy } from "@vega-core/utils/isTruthy";

import {
  ThemedDropdown,
  type ThemedDropdownProps,
} from "../ThemedDropdown/ThemedDropdown";

type Props = {
  label?: string;
  labelTag?: ReactNode;
  name: string;
  compact?: boolean;
  isOptional?: boolean;
  options?: OptionType[];
  placeholder?: string;
  withBorders?: boolean;
  disabled?: boolean;
  withCheckmark?: boolean;
  showDescription?: boolean;
  isSearchable?: boolean;
  onChange?: (value?: string) => void;
  emptyListPlaceholder?: ReactElement;
  fullWidth?: boolean;
  constrainInputWidth?: boolean;
};

/**
 * @deprecated This component is deprecated. Please use ThemedDropdownField instead.
 */
export function DropdownField({
  label,
  labelTag,
  name,
  compact,
  options,
  isOptional,
  placeholder,
  disabled,
  isSearchable,
  emptyListPlaceholder,
  withBorders,
  withCheckmark,
  showDescription,
  fullWidth,
  constrainInputWidth = false,
}: Props) {
  const [field, meta, helpers] = useField(name);

  return (
    <Dropdown
      compact={compact}
      constrainInputWidth={constrainInputWidth}
      disabled={disabled}
      emptyListPlaceholder={emptyListPlaceholder}
      error={meta.touched && isTruthy(meta.error) ? meta.error : undefined}
      fullWidth={fullWidth}
      isOptional={isOptional}
      isSearchable={isSearchable}
      label={label}
      labelTag={labelTag}
      name={name}
      onChange={(val) => helpers.setValue(val.value)}
      options={options}
      placeholder={placeholder}
      showDescription={showDescription}
      value={field.value}
      withBorders={withBorders !== undefined ? withBorders : true}
      withCheckmark={withCheckmark}
    />
  );
}

export function ThemedDropdownField({
  name,
  onChange,
  ...props
}: Omit<ThemedDropdownProps, "onChange"> & {
  onChange?: (value?: string) => void;
}) {
  const [field, meta, helpers] = useField(name);
  useTouchFieldOnSubmit(name);

  return (
    <ThemedDropdown
      {...props}
      error={meta.touched && isTruthy(meta.error) ? meta.error : undefined}
      name={name}
      onChange={(val) => {
        helpers.setValue(val?.value);
        onChange && onChange(val?.value);
      }}
      value={field.value}
    />
  );
}

export function SearchableDropdownField({
  name,
  onChange,
  ...props
}: Omit<SearchableDropdownProps, "onChange" | "value"> & {
  onChange?: (value?: string) => void;
}) {
  const [field, meta, helpers] = useField(name);
  useTouchFieldOnSubmit(name);

  return (
    <SearchableDropdown
      {...props}
      error={meta.touched && isTruthy(meta.error) ? meta.error : undefined}
      name={name}
      onChange={(val) => {
        helpers.setValue(val?.value);
        onChange && onChange(val?.value);
      }}
      value={props.options?.find((option) => option.value === field.value)}
    />
  );
}
